import { type FC, useState } from 'react';
import * as uuid from 'uuid';
import { BsPlus } from 'react-icons/bs';
import { useDispatch } from 'react-redux';
import { Button, Form } from 'react-bootstrap';
import { DragDropContext } from 'react-beautiful-dnd';
import DelayedDroppableWrapper from '@editor2/components/DelayedDroppableWrapper';
import cx from 'clsx';

import CodeMirrorHTML from '@editor2/components/CodeMirrorHTML';
import CopyInput from '@editor2/components/CopyInputWrapper';
import Metadata from '@editor2/components/Metadata';
import { useDebouncedDispatch } from '@editor2/hooks/useDebouncedDispatch';
import { useDebounceProtectedState } from '@editor2/hooks/useDebounceProtectedState';
import { pollToPollProperties } from '@editor2/mappers/element';
import { updateElement } from '@editor2/reducers/editor/editorSlice';
import { type Poll, type PollChoicePayload, ElementType } from '@editor2/types/pageElements';

import DatasetContextualInfo from './DatasetContextualInfo';
import PollChoiceElement from './PollChoice';
import PollDataTable from './PollDataTable';
import RemoveDataModal from './RemoveDataModal';
import { pollElement } from './styles';
import {
	type PollDataType,
	POLL_DATA_GROUP_BY_MAP,
	POLL_DATA_TYPE_MAP,
	POLL_CHOICE_ORDERING_MAP
} from './types';
import ColorPaletteConfig from './ColorPaletteConfig';
import PollResultsChartPreview from './PollResultsChartPreview';
import { getTotalRowAfterDataTypeChange } from './helpers';
import { elementCommonStyles } from '@editor2/styles/element';

interface Props {
	element: Poll;
	courseId: number;
}

const PollElement: FC<Props> = (props) => {
	const { element, courseId } = props;

	const commonPayload = { elementId: element.family_id, courseId };

	const [pollProperties, handleLocalChange] = useDebounceProtectedState(
		element,
		pollToPollProperties
	);
	const [isRemoveDataModalShown, setRemoveDataModalShown] = useState(false);

	const dispatch = useDispatch();
	const debouncedDispatch = useDebouncedDispatch();

	const onChange = (value) => {
		dispatch(updateElement({ ...commonPayload, ...value }));
	};
	const onDebouncedChange = (value) => {
		debouncedDispatch(updateElement({ ...commonPayload, ...value }));
	};

	const handleDebouncedChange = (value) => {
		handleLocalChange(value);
		onDebouncedChange(value);
	};

	const handleAddChoiceClick = () => {
		const newChoice: PollChoicePayload = {
			family_id: uuid.v4(),
			body: '',
			shortened_body: '',
			metadata: {},
			type: ElementType.PollChoice
		};

		onChange({
			choices: [...element.choices, newChoice],
			...(element.data && {
				data: [...element.data, Array(element.data[0].length).fill('')]
			})
		});
	};

	const handleDragEnd = (result) => {
		const { source, destination, type } = result;
		const { index: sourceIndex } = source;
		const { index: destinationIndex } = destination;

		if (type === 'PollChoice') {
			const choices = [
				...element.choices.slice(0, sourceIndex),
				...element.choices.slice(sourceIndex + 1)
			];

			const data = element.data && [
				...element.data.slice(0, sourceIndex + 1),
				...element.data.slice(sourceIndex + 2)
			];

			onChange({
				choices: [
					...choices.slice(0, destinationIndex),
					element.choices[sourceIndex],
					...choices.slice(destinationIndex)
				],
				...(element.data && {
					data: [
						...data.slice(0, destinationIndex + 1),
						element.data[sourceIndex + 1],
						...data.slice(destinationIndex + 1)
					]
				})
			});
		}
	};

	const handleAddDataClick = () => {
		const initialData = Array(element.choices.length + 1).fill(['']);
		onChange({
			data: initialData,
			total_count: [0],
			data_type: 'number',
			data_group_by: 'dataset',
			choice_ordering: 'popularity_desc',
			source_dataset_index: 1,
			contextual_info: ['']
		});
	};

	const handleRemoveDataClick = () => {
		setRemoveDataModalShown(true);
	};

	return (
		<Form className={cx(pollElement, elementCommonStyles)}>
			<CopyInput label="Family ID" value={pollProperties.family_id} />

			<Metadata
				courseId={courseId}
				metadata={element.metadata}
				onDebouncedChange={onDebouncedChange}
			/>

			<Form.Group className="questionBody">
				<Form.Label>Question</Form.Label>
				<CodeMirrorHTML
					value={pollProperties.body}
					showTips={false}
					options={{ placeholder: 'Type your question here', lineNumbers: false, gutters: [] }}
					onChange={(value) => handleDebouncedChange({ body: value })}
				/>
			</Form.Group>

			<div className="choiceLabels">
				<Form.Label>Choices</Form.Label>
				<Form.Label>Shortened version</Form.Label>
			</div>
			<DragDropContext onDragEnd={handleDragEnd}>
				<DelayedDroppableWrapper droppableId={element.family_id} type="PollChoice">
					{(provided) => (
						<div ref={provided.innerRef} {...provided.droppableProps}>
							{element.choices.map((choice, i) => (
								<PollChoiceElement
									choice={choice}
									courseId={courseId}
									element={element}
									index={i}
									key={choice.family_id}
								/>
							))}
							{provided.placeholder}
						</div>
					)}
				</DelayedDroppableWrapper>
			</DragDropContext>

			<Button
				variant="outline-secondary"
				onClick={() => handleAddChoiceClick()}
				className="addChoiceButton">
				<BsPlus />
				Add choice
			</Button>

			<ColorPaletteConfig
				courseId={courseId}
				color_palette={element.color_palette}
				color_group_by={element.color_group_by}
				chart_type={element.chart_type}
				onChange={onChange}
			/>

			<PollResultsChartPreview element={element} courseId={courseId} />

			<div className="addDataBlock">
				{!element.data && (
					<Button
						variant="outline-primary"
						className="addDataButton"
						disabled={element.chart_type === 'pie' || !element.chart_type}
						onClick={() => handleAddDataClick()}>
						Add data sets
					</Button>
				)}

				{element.data && (
					<>
						<Button
							variant="outline-danger"
							className="removeDataButton"
							onClick={() => handleRemoveDataClick()}>
							Remove data sets
						</Button>

						<RemoveDataModal
							onHide={() => setRemoveDataModalShown(false)}
							isShown={isRemoveDataModalShown}
							courseId={courseId}
							elementId={element.family_id}
						/>
					</>
				)}

				<Form.Group className="chartTypeBlock">
					<Form.Label>Chart type:</Form.Label>
					<Form.Check
						type="radio"
						checked={element.chart_type === 'bar'}
						onChange={() => onChange({ chart_type: 'bar' })}
						label="bar"
					/>
					<Form.Check
						type="radio"
						checked={element.chart_type === 'pie'}
						onChange={() => onChange({ chart_type: 'pie', color_group_by: 'response' })}
						label="pie"
						disabled={!!element.data}
					/>
				</Form.Group>
			</div>

			{element.data && (
				<Form.Group>
					<Form.Label>Data:</Form.Label>
					<PollDataTable
						data={element.data}
						total_count={element.total_count}
						data_type={element.data_type}
						contextual_info={element.contextual_info}
						choices={element.choices}
						onChange={onChange}
					/>
				</Form.Group>
			)}

			<div className="dataSettings">
				{element.data && (
					<>
						<Form.Group>
							<Form.Label>Data type:</Form.Label>
							<Form.Control
								as="select"
								value={element.data_type}
								onChange={(e) => {
									const updatedTotalRow = getTotalRowAfterDataTypeChange(
										e.target.value as PollDataType,
										element.data
									);
									onChange({ data_type: e.target.value, total_count: updatedTotalRow });
								}}>
								{Object.keys(POLL_DATA_TYPE_MAP).map((type) => (
									<option key={type} value={type}>
										{POLL_DATA_TYPE_MAP[type]}
									</option>
								))}
							</Form.Control>
						</Form.Group>

						<Form.Group>
							<Form.Label>Group data by:</Form.Label>
							<Form.Control
								as="select"
								value={element.data_group_by}
								onChange={(e) =>
									onChange({
										data_group_by: e.target.value,
										choice_ordering: e.target.value === 'dataset' ? 'popularity_desc' : null
									})
								}>
								{Object.keys(POLL_DATA_GROUP_BY_MAP).map((type) => (
									<option key={type} value={type}>
										{POLL_DATA_GROUP_BY_MAP[type]}
									</option>
								))}
							</Form.Control>
						</Form.Group>
					</>
				)}

				{(!element.data || element.data_group_by === 'dataset') && (
					<Form.Group>
						<Form.Label>Order poll choices by:</Form.Label>
						<Form.Control
							as="select"
							value={element.choice_ordering}
							onChange={(e) =>
								onChange({
									choice_ordering: e.target.value,
									...(element.data_group_by === 'dataset' && {
										source_dataset_index: e.target.value !== 'fixed' ? 1 : null
									})
								})
							}>
							{Object.keys(POLL_CHOICE_ORDERING_MAP).map((type) => (
								<option key={type} value={type}>
									{POLL_CHOICE_ORDERING_MAP[type]}
								</option>
							))}
						</Form.Control>
					</Form.Group>
				)}

				{element.data &&
					element.data_group_by === 'dataset' &&
					element.choice_ordering !== 'fixed' && (
						<Form.Group>
							<Form.Label>Source dataset:</Form.Label>
							<Form.Control
								as="select"
								value={element.source_dataset_index}
								onChange={(e) => onChange({ source_dataset_index: e.target.value })}>
								<option key={0} value={0}>
									Class
								</option>

								{element.data[0].map((title, index) => (
									<option key={index + 1} value={index + 1}>
										{title}
									</option>
								))}
							</Form.Control>
						</Form.Group>
					)}
			</div>

			{element.data && (
				<>
					<Form.Label>Contextual info</Form.Label>
					{element.data[0].map((_, index) => (
						<DatasetContextualInfo
							key={`${element.data[0].length}-${index}`}
							index={index}
							contextual_info={element.contextual_info}
							onChange={handleDebouncedChange}
						/>
					))}
				</>
			)}

			<Form.Group>
				<Form.Label>Instructions</Form.Label>
				<CodeMirrorHTML
					value={pollProperties.instructions}
					showTips={false}
					options={{ placeholder: 'Poll instructions' }}
					onChange={(value) => handleDebouncedChange({ instructions: value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Caption</Form.Label>
				<CodeMirrorHTML
					value={pollProperties.caption}
					showTips={false}
					options={{ placeholder: 'Poll caption' }}
					onChange={(value) => handleDebouncedChange({ caption: value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Description</Form.Label>
				<CodeMirrorHTML
					value={pollProperties.description}
					showTips={false}
					options={{ placeholder: 'Poll description' }}
					onChange={(value) => handleDebouncedChange({ description: value })}
				/>
			</Form.Group>

			<Form.Group>
				<Form.Label>Credits</Form.Label>
				<CodeMirrorHTML
					value={pollProperties.credits}
					showTips={false}
					options={{ placeholder: 'Poll credits' }}
					onChange={(value) => handleDebouncedChange({ credits: value })}
				/>
			</Form.Group>
		</Form>
	);
};

export default PollElement;
